import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Star } from "lucide-react";
import { toast } from "sonner";

export default function Markets() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/market/quotes")
      .then(({ data }) => setQuotes(data))
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = quotes
    .filter((q) => (tab === "all" ? true : q.asset_type === tab))
    .filter(
      (q) =>
        !filter ||
        q.symbol.toLowerCase().includes(filter.toLowerCase()) ||
        q.name.toLowerCase().includes(filter.toLowerCase())
    );

  const addWatch = async (q) => {
    try {
      await api.post("/watchlist", { symbol: q.symbol, asset_type: q.asset_type });
      toast.success(`${q.symbol} added to watchlist`);
    } catch {
      toast.error("Could not add");
    }
  };

  return (
    <div className="space-y-8" data-testid="markets-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Markets</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2">Browse assets</h1>
        <p className="text-gray-500 mt-2">Real-time crypto (CoinGecko) and stocks. Add favorites to your watchlist.</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {["all", "crypto", "stock", "future"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`rounded-full text-xs font-medium px-4 py-2 transition-colors ${
                tab === t ? "bg-brand text-white" : "text-gray-600"
              }`}
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>
        <Input
          data-testid="markets-search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search symbol or name"
          className="max-w-xs h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand"
        />
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading markets…</p>
      ) : (
        <Card className="border border-gray-200 rounded-2xl divide-y divide-gray-100">
          {filtered.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">No matches.</p>
          ) : (
            filtered.map((q) => (
              <div
                key={`${q.asset_type}:${q.symbol}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                data-testid={`market-row-${q.symbol}`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold text-lg">{q.symbol}</span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                      {q.asset_type}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{q.name}</p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-mono tabular-nums">
                    {fmtUSD(q.price, { dp: q.price > 10 ? 2 : 4 })}
                  </p>
                  <button
                    onClick={() => addWatch(q)}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-brand-deep"
                    data-testid={`watch-${q.symbol}`}
                    title="Watch"
                  >
                    <Star size={14} />
                  </button>
                  <Link
                    to={`/trade?symbol=${q.symbol}&type=${q.asset_type}`}
                    data-testid={`trade-${q.symbol}`}
                    className="text-xs font-medium bg-brand text-white rounded-full px-4 py-2 hover:bg-brand-deep"
                  >
                    Trade
                  </Link>
                </div>
              </div>
            ))
          )}
        </Card>
      )}
    </div>
  );
}
